import { useEffect, useState, useRef, useMemo } from "react";
import toast from "react-hot-toast";
import { createClient } from "@supabase/supabase-js";
import { useSearchParams, useParams } from "react-router";
import axios from "axios";
import Topbar from "./Topbar";
import XPBar from "./XPBar";
import HoloCard from "./HoloCard";
import HoloCardInspect from "./HoloCardInspect";
import { getXPProgress } from "./leveling";
import { loadStripe } from "@stripe/stripe-js";
import PurchaseSuccessModal from "./PurchaseSuccessModal";
import Footer from "./Footer";
import Loader from "./Loader";
const apiBase = process.env.REACT_APP_API_URL;
const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY);
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function BattlepassRewardsPageEmpty() {
  const { leagueId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [purchaseData, setPurchaseData] = useState({ coins: 0, amount: 0 });
  const [tiers, setTiers] = useState([]);
  const [inspectedCard, setInspectedCard] = useState(null);
  const [items, setItems] = useState([]);
  const [profile, setProfile] = useState(null);
  const [battlepass, setBattlepass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bundlePreviews, setBundlePreviews] = useState({});
  const [error, setError] = useState("");

  const firstUnlockedRef = useRef(null);

  useEffect(() => {
    const coins = parseInt(searchParams.get("coins")) || 0;
    const amount = parseInt(searchParams.get("amount")) || 0;

    if (coins > 0) {
      setPurchaseData({ coins, amount });
      setShowModal(true);

      // remove query params from URL so modal doesn't show again on refresh
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (firstUnlockedRef.current) {
      firstUnlockedRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [tiers, profile]);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError("");

        // 1. Get session + token first
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();
        if (sessionError || !session) throw new Error("No active session found");

        const token = session.access_token;

        // 2. Fetch battlepass + profile in parallel
        const [bpRes, skinsShop, profileRes] = await Promise.all([
          axios.get(`${apiBase}/api/battlepass/active`),
          axios.get(`${apiBase}/api/skins/shop`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiBase}/api/profile/me`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        if (!bpRes.data?.id) throw new Error("No active battlepass found");
        setBattlepass(bpRes.data);
        setItems(skinsShop.data);
        setProfile(profileRes.data.profile);

        const collections = [...new Set(skinsShop.data.map((s) => s.collection_id))];

        const previews = {};

        for (const collectionId of collections) {
          const previewRes = await axios.post(
            `${apiBase}/api/skin-bundle-preview`,
            { collectionId },
            { headers: { Authorization: `Bearer ${token}` } },
          );

          previews[collectionId] = previewRes.data;
        }

        setBundlePreviews(previews);

        // 3. Fetch tiers (needs battlepass id + token)
        const tiersRes = await axios.get(`${apiBase}/api/battlepass/${bpRes.data.id}/tiers-with-claims`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTiers(tiersRes.data);
      } catch (err) {
        console.error(err);
        setError("Could not load battlepass rewards.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  // Group skins by player name
  const groupedSkins = useMemo(() => {
    return items.reduce((acc, skin) => {
      const key = skin.collection_id;
      if (!acc[key]) acc[key] = [];
      acc[key].push(skin);
      return acc;
    }, {});
  }, [items]);

  if (loading) return <Loader loadingTitle={"Loading rewards"} showLogo={false} />;
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  if (!profile) return null;
  const xpStats = getXPProgress(profile.xp || 0);
  const userLevel = xpStats.level;

  async function handleClaim(tierId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");
      const token = session.access_token;

      await axios.post(
        `${apiBase}/api/battlepass/${battlepass.id}/claim/${tierId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast.success("You claimed a reward! 🎉", {
        style: {
          border: "2px solid gold",
          padding: "12px",
          color: "#fff",
          background: "#111827",
        },
        iconTheme: {
          primary: "#FFD700",
          secondary: "#111827",
        },
      });
      // update state
      setTiers((prev) => prev.map((t) => (t.id === tierId ? { ...t, claimed: true } : t)));
    } catch (err) {
      console.error("Error claiming reward:", err);
      alert(err.response?.data?.error || err.message || "Failed to claim reward");
    }
  }

  // async function handleBuyCoins(pack) {
  //   try {
  //     const {
  //       data: { session },
  //     } = await supabase.auth.getSession();
  //     if (!session) throw new Error("No active session");
  //     const token = session.access_token;

  //     // call backend to create checkout session
  //     const res = await axios.post(
  //       `${apiBase}/api/create-checkout-session`,
  //       { pack },
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //       },
  //     );

  //     const stripe = await stripePromise;
  //     await stripe.redirectToCheckout({ sessionId: res.data.id });
  //   } catch (err) {
  //     console.error("Error creating checkout session:", err);
  //     alert(err.response?.data?.error || err.message);
  //   }
  // }

  async function handleBuyXP(pack) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");
      const token = session.access_token;

      // call backend to create checkout session
      const res = await axios.post(
        `${apiBase}/api/create-checkout-session-training`,
        { pack },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const stripe = await stripePromise;
      await stripe.redirectToCheckout({ sessionId: res.data.id });
    } catch (err) {
      console.error("Error creating checkout session:", err);
      alert(err.response?.data?.error || err.message);
    }
  }

  async function handleBuyBundle(collectionId) {
    if (!collectionId) return alert("Collection name is required");

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");
      const token = session.access_token;

      const res = await axios.post(
        `${apiBase}/api/create-skin-bundle-session`,
        { collectionId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const stripe = await stripePromise;
      await stripe.redirectToCheckout({ sessionId: res.data.id });
    } catch (err) {
      console.error("Error buying bundle:", err);
      alert(err.response?.data?.error || err.message);
    }
  }

  async function handleBuySkin(skinId) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) throw new Error("No active session");

      const token = session.access_token;

      const res = await axios.post(`${apiBase}/api/create-skin-session`, { skinId }, { headers: { Authorization: `Bearer ${token}` } });

      const stripe = await stripePromise;
      await stripe.redirectToCheckout({ sessionId: res.data.id });
    } catch (err) {
      console.error("Error buying skin:", err);
      alert(err.response?.data?.error || err.message);
    }
  }

  return (
    <div
      style={{
        maxWidth: "1920px",
        margin: "auto",
        boxSizing: "border-box",
        display: "flex",
        minHeight: "100vh",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "0 20px",
      }}>
      <div>
        <Topbar leagueId={leagueId} showLinks={false} showEmptyRewards={true} />
        <div className="battlepass-page" style={{ maxWidth: "1200px", margin: "auto" }}>
          {showModal && <PurchaseSuccessModal coins={purchaseData.coins} amount={purchaseData.amount} onClose={() => setShowModal(false)} />}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <XPBar xp={profile.xp || 0} />
            <div
              style={{
                marginBottom: "10px",
                position: "relative",
              }}
              className="fade-out-right">
              <div className="tiers-list">
                {tiers.map((tier, idx) => {
                  const locked = userLevel < tier.level_required;
                  const isFirstUnlocked = !locked && !tier.claimed && tiers.findIndex((t) => userLevel >= t.level_required && !t.claimed) === idx;

                  return (
                    <div
                      key={tier.id}
                      ref={isFirstUnlocked ? firstUnlockedRef : null}
                      className={`tier-card ${tier.claimed ? "claimed" : ""} ${locked ? "locked" : ""}`}
                      onClick={() => !tier.claimed && !locked && handleClaim(tier.id)}
                      style={{ cursor: tier.claimed || locked ? "default" : "pointer" }}
                      role="button"
                      tabIndex={tier.claimed || locked ? -1 : 0}
                      onKeyDown={(e) => !tier.claimed && !locked && e.key === "Enter" && handleClaim(tier.id)}>
                      <h3
                        style={
                          tier.claimed
                            ? { color: "white", margin: "0", fontSize: "12px", opacity: "0.5" }
                            : { margin: "0", fontSize: "12px", color: "#fde7ba" }
                        }>
                        {/* TIER {tier.level_required} */}
                      </h3>
                      {tier.reward_type === "avatar" ? (
                        <img src={`/avatars/${tier.reward.image_path}`} alt={tier.reward.name} />
                      ) : tier.reward_type === "skin" ? (
                        <img src={`/players/${tier.reward.image_path}`} alt={tier.reward.name} />
                      ) : tier.reward_type === "coins" ? (
                        <img src="/mini-tokens.png" alt="League Tokens" style={{ border: "none" }} />
                      ) : (
                        <></>
                      )}
                      {tier.claimed ? (
                        <button disabled className="battlepass-claim-btn" style={{ color: "rgba(255, 255, 255, 0.1)" }}>
                          Claimed
                        </button>
                      ) : (
                        <button disabled={locked} onClick={() => handleClaim(tier.id)} className="battlepass-claim-btn">
                          {locked ? "🔒" : "Click to claim"}
                        </button>
                      )}

                      <span style={tier.claimed ? { background: "black", opacity: "0.2" } : {}}>
                        <p style={{ margin: "0", fontSize: "10px" }}>
                          XP: <b style={{ color: "white", fontSize: "10px" }}>Lvl {tier.level_required}</b>
                        </p>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="store-container">
              <div className="store-grid-parent">
                <p style={{ margin: "10px 0", fontSize: "12px", color: "white", textAlign: "center" }}>
                  *The purchase of player cards does not assign the player to your active team. These are cosmetics you can apply to your player once
                  you own them
                </p>
                <img
                  src="/first-stand-2026-promo.webp"
                  style={{ width: "100%", objectFit: "contain", borderRadius: "8px", marginBottom: "40px" }}
                  alt="First Stand Collection"
                />
                <div
                  className="coin-store"
                  style={{
                    background: "#0b131e",
                    padding: "20px 10px",
                    borderRadius: "12px",
                    boxSizing: "border-box",
                  }}>
                  {Object.entries(groupedSkins).map(([playerName, skins]) => {
                    const bundlePreview = bundlePreviews[playerName];
                    return (
                      <div key={playerName} style={{ marginBottom: "32px" }}>
                        {playerName === "LPL Chinese New Year" ? (
                          <img src="/chinese-new-year-collection-title.png" style={{ height: "20px" }} alt="Chinese New Year Collection" />
                        ) : (
                          <img src="/first-stand-title.png" style={{ height: "20px" }} alt="First Stand Collection" />
                        )}
                        {bundlePreview && !bundlePreview.isComplete && (
                          <>
                            <h2
                              style={{
                                color: "white",
                                borderBottom: "1px solid #ffffff1a",
                                paddingBottom: "6px",
                                marginBottom: "20px",
                                letterSpacing: "-0.4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                fontSize: "21px",
                              }}>
                              Purchase bundle w/ Bonus Coins
                            </h2>
                            <div className="store-item" style={{ border: "2px solid rgb(235, 195, 115)" }}>
                              {playerName === "LPL Chinese New Year" ? (
                                <img
                                  src="/lpl-chinese-bundle.webp"
                                  style={{ width: "100%", objectFit: "contain", borderRadius: "12px" }}
                                  id="mobile-bundle-img"
                                  alt="Chinese New Year Collection"
                                />
                              ) : (
                                <img
                                  src="/first-stand-26-bundle.webp"
                                  style={{ width: "100%", objectFit: "contain", borderRadius: "12px" }}
                                  id="mobile-bundle-img"
                                  alt="Chinese New Year Collection"
                                />
                              )}
                              <div style={{ padding: "5px 10px", background: "#0000008a", borderRadius: "6px", width: "100%" }}>
                                <p style={{ margin: 0, fontSize: "21px", color: "#ebc373" }}>
                                  Buy Bundle ({bundlePreview.skinCount} skins + {bundlePreview.bonusCoins} Bonus Coins)
                                </p>
                                {bundlePreview && (
                                  <div className="bundle-preview">
                                    <p>
                                      Get {bundlePreview.skinCount >= 5 ? "all 5 player cards" : `${bundlePreview.skinCount} remaining player cards`}
                                    </p>
                                  </div>
                                )}
                                {bundlePreview?.isComplete ? (
                                  <button disabled className="store-btn-disabled" style={{ opacity: "0.6", cursor: "default" }}>
                                    Bundle Completed
                                  </button>
                                ) : (
                                  <button onClick={() => handleBuyBundle(playerName)}>${(bundlePreview.totalPrice / 100).toFixed(2)}</button>
                                )}
                              </div>
                            </div>
                          </>
                        )}

                        <h2
                          style={{
                            color: "white",
                            borderBottom: "1px solid #ffffff1a",
                            paddingBottom: "6px",
                            marginBottom: "20px",
                            marginTop: "30px",
                            letterSpacing: "-0.4px",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "21px",
                          }}>
                          Or purchase individual cards
                        </h2>
                        <div className="coin-package-grid" style={{ display: "flex", gap: "16px", flexWrap: "wrap", justifyContent: "center" }}>
                          {skins.map((skin) => (
                            <div
                              key={skin.id}
                              onClick={() =>
                                setInspectedCard({
                                  image: `/players/${skin.image_url}`,
                                  foil: `/players/${skin.foil_url}`,
                                  mask: `/players/${skin.mask_url}`,
                                  topLayer: `/players/cards/${skin.top_layer}`,
                                  rarity: skin.rarity,
                                })
                              }
                              style={{ cursor: "pointer", position: "relative", border: "1px solid rgb(235, 195, 115)" }}
                              className="store-item">
                              <span
                                style={{
                                  position: "absolute",
                                  top: "10px",
                                  zIndex: "100",
                                  color: "white",
                                  borderRadius: "90px",
                                  background: "#2A6FFF",
                                  padding: "2px 8px",
                                  fontSize: "12px",
                                  fontWeight: "700",
                                  left: "-10px",
                                  border: "1px solid white",
                                }}>
                                Click for preview
                              </span>
                              <HoloCard
                                image={`/players/${skin.image_url}`}
                                foil={`/players/${skin.foil_url}`}
                                mask={`/players/${skin.mask_url}`}
                                topLayer={`/players/cards/${skin.top_layer}`}
                                rarity={skin.rarity}
                                width={160}
                                height={259}
                                borderRadius="10px"
                              />
                              {skin.owned ? (
                                <span className="owned-label">Owned</span>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBuySkin(skin.id);
                                  }}>
                                  ${(skin.price / 100).toFixed(2)}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* <div className="coin-store">
                  <h2
                    style={{
                      color: "white",
                      borderBottom: "1px solid #ffffff1a",
                      paddingBottom: "6px",
                      marginBottom: "20px",
                      letterSpacing: "-0.4px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}>
                    <img src="/small-tokens.png" style={{ height: "32px", objectFit: "contain" }} alt="League Currency" /> Token Packs
                  </h2>
                  <div className="coin-package-grid">
                    <div className="store-item">
                      <h3>Matchday Boost</h3>
                      <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                        <h2>250</h2>
                      </div>
                      <img
                        src="/mini-tokens.png"
                        alt="Swap Credit"
                        style={{ height: "74px", objectFit: "contain", filter: "drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))" }}
                      />
                      <button onClick={() => handleBuyCoins("mini")}>$2.99</button>
                    </div>
                    <div className="store-item">
                      <h3>Draft Advantage</h3>
                      <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                        <h2>600</h2>
                      </div>
                      <h4
                        style={{
                          color: "#3273fa",
                          zIndex: "99",
                        }}>
                        +100 BONUS COINS
                      </h4>
                      <img
                        src="/small-tokens.png"
                        alt="Swap Credit"
                        style={{ height: "90px", objectFit: "contain", filter: "drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))" }}
                      />
                      <button onClick={() => handleBuyCoins("small")}>$5.99</button>
                    </div>
                    <div className="store-item">
                      <div className="background-fade" style={{ borderRadius: "12px" }} />
                      <h3>Meta Breaker</h3>
                      <div style={{ display: "flex", alignItems: "center", flexDirection: "row", gap: "10px", zIndex: "99" }}>
                        <img src="/sale-burst.png" alt="Swap Credit" style={{ height: "36px", objectFit: "contain" }} />
                        <h2>1600</h2>
                      </div>
                      <h4
                        style={{
                          color: "#3273fa",
                          zIndex: "99",
                        }}>
                        +350 BONUS COINS
                      </h4>
                      <img
                        src="/many-tokens.png"
                        alt="Swap Credit"
                        style={{ height: "90px", objectFit: "contain", filter: "drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))", zIndex: "99" }}
                      />
                      <button onClick={() => handleBuyCoins("medium")}>$9.99</button>
                    </div>
                    <div className="store-item">
                      <h3>Tournament Stack</h3>
                      <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                        <h2>4000</h2>
                      </div>
                      <h4
                        style={{
                          color: "#3273fa",
                          zIndex: "99",
                        }}>
                        +1000 BONUS COINS
                      </h4>
                      <img
                        src="/t-stack.png"
                        alt="Swap Credit"
                        style={{ height: "90px", objectFit: "contain", filter: "drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))" }}
                      />
                      <button onClick={() => handleBuyCoins("large")}>$19.99</button>
                    </div>
                    <div className="store-item">
                      <h3>Championship Vault</h3>
                      <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                        <h2>9000</h2>
                      </div>
                      <h4
                        style={{
                          color: "#3273fa",
                          zIndex: "99",
                        }}>
                        +2000 BONUS COINS
                      </h4>
                      <img
                        src="/tournament-stack.png"
                        alt="Swap Credit"
                        style={{ height: "90px", objectFit: "contain", filter: "drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))" }}
                      />
                      <button onClick={() => handleBuyCoins("huge")}>$49.95</button>
                    </div>
                  </div>
                </div> */}
                <div className="coin-store">
                  <h2
                    style={{
                      color: "white",
                      borderBottom: "1px solid #ffffff1a",
                      paddingBottom: "6px",
                      margin: "40px 0 20px 0",
                      letterSpacing: "-0.4px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}>
                    <img src="/small-boosts.png" style={{ height: "32px", objectFit: "contain" }} alt="League Currency" />
                    Instant Training Packs
                  </h2>
                  <div className="coin-package-grid">
                    <div className="store-item">
                      <h3>Matchday Boost</h3>
                      <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                        <h2>20</h2>
                      </div>
                      <img
                        src="/mini-boosts.png"
                        alt="XP Boost"
                        style={{ height: "110px", objectFit: "contain", filter: "drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))" }}
                      />
                      <button onClick={() => handleBuyXP("mini")}>$2.99</button>
                    </div>
                    <div className="store-item">
                      <h3>Draft Advantage</h3>
                      <div style={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                        <h2>100</h2>
                      </div>
                      <img
                        src="/small-boosts.png"
                        alt="Swap Credit"
                        style={{ height: "110px", objectFit: "contain", filter: "drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))" }}
                      />
                      <button onClick={() => handleBuyXP("small")}>$5.99</button>
                    </div>
                    <div className="store-item">
                      <div className="background-fade" style={{ borderRadius: "12px" }} />
                      <h3>Meta Breaker</h3>
                      <div style={{ display: "flex", alignItems: "center", flexDirection: "row", gap: "10px", zIndex: "99" }}>
                        <img src="/sale-burst.png" alt="Swap Credit" style={{ height: "36px", objectFit: "contain" }} />
                        <h2>225</h2>
                      </div>
                      <img
                        src="/many-boosts.png"
                        alt="Swap Credit"
                        style={{ height: "110px", objectFit: "contain", filter: "drop-shadow(0 0 20px rgba(255, 255, 255, 0.2))", zIndex: "99" }}
                      />
                      <button onClick={() => handleBuyXP("medium")}>$9.99</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      {inspectedCard && <HoloCardInspect {...inspectedCard} onClose={() => setInspectedCard(null)} />}
    </div>
  );
}
